import type { HTMLAttributes, ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils.js';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  className?: string;
}

/**
 * A shadcn-shaped Dialog whose open/close transition is driven by motion.dev rather than CSS
 * keyframes. Radix owns focus-trapping and a11y; `forceMount` keeps the DOM node mounted while
 * AnimatePresence plays the exit animation, since AnimatePresence can only animate the removal
 * of its own direct children — see NewProjectDialog for a caller.
 */
export function Dialog({ open, onOpenChange, children, className }: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-40 bg-black/40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              {/* Positioning lives on this plain element so its centering transform isn't
                  clobbered by the inline `transform` that motion writes onto the animated
                  child below (inline styles beat the Tailwind translate classes). */}
              <div
                className={cn(
                  'fixed left-1/2 top-1/2 z-40 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2',
                  className,
                )}
              >
                <motion.div
                  // Capped to the visible viewport (the app height tracks the on-screen keyboard,
                  // see lib/viewport.ts) with the content scrolling inside, so a tall dialog on a
                  // phone never pushes its own buttons off-screen. The scroll lives on an inner
                  // wrapper so the close button stays put while the content moves.
                  className="relative flex max-h-[calc(var(--app-height,100dvh)-2rem)] flex-col overflow-hidden rounded-card border border-border bg-surface shadow-lg outline-none"
                  initial={{ opacity: 0, scale: 0.96, y: 8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96, y: 8 }}
                  transition={{ duration: 0.16, ease: 'easeOut' }}
                >
                  <div className="min-h-0 overflow-y-auto overscroll-contain p-5 sm:p-6">{children}</div>
                  <DialogPrimitive.Close className="absolute right-2 top-2 flex h-10 w-10 items-center justify-center rounded-md text-muted outline-none transition-colors hover:text-text focus-visible:ring-2 focus-visible:ring-focus sm:right-3 sm:top-3 sm:h-8 sm:w-8">
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </DialogPrimitive.Close>
                </motion.div>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5', className)} {...props} />;
}

export function DialogTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <DialogPrimitive.Title className={cn('text-base font-semibold', className)} {...props} />;
}

export function DialogDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <DialogPrimitive.Description className={cn('text-sm text-muted', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-wrap justify-end gap-2', className)} {...props} />;
}
